import * as Api from '@ViewModels';
import * as React from 'react';
import {
	getManagedAccountAccessSession,
	isManagedAccountAccessCredential,
} from '../../models/ManagedAccountAccessSession';
import type { IManagedAccountAccessSession } from '../../models/ManagedAccountAccessSession';
import { useUserSession } from '../../models/hooks/appStateHooks';

interface IManagedAccountAccessSessionState {
	credential: Api.ICredential | null;
	expiresUtc: number | null;
	isLoading: boolean;
	isManagedAccountAccess: boolean;
	session: IManagedAccountAccessSession | null;
}

export function useManagedAccountAccessSessionState(): IManagedAccountAccessSessionState {
	const userSession = useUserSession();
	const [state, setState] = React.useState<{
		credential: Api.ICredential | null;
		isLoading: boolean;
		session: IManagedAccountAccessSession | null;
	}>({
		credential: null,
		isLoading: true,
		session: null,
	});

	React.useEffect(() => {
		let mounted = true;

		async function loadSessionState(): Promise<void> {
			const [credential, session] = await Promise.all([
				userSession.webServiceHelper
					.getCredentialStore()
					.getCredential()
					.catch((): null => null),
				getManagedAccountAccessSession(),
			]);

			if (!mounted) {
				return;
			}

			setState({
				credential,
				isLoading: false,
				session,
			});
		}

		loadSessionState();

		return () => {
			mounted = false;
		};
	}, [userSession, userSession?.account?.id, userSession?.isAuthenticated, userSession?.user?.id]);

	return {
		...state,
		expiresUtc: state.session?.expiresUtc ?? state.credential?.expires_utc ?? null,
		isManagedAccountAccess: isManagedAccountAccessCredential(state.credential),
	};
}
